/* src/modules/it/components/ITInventory.js */
import React, { useState, useEffect, useRef } from 'react';
import { 
    collection, addDoc, query, orderBy, onSnapshot, 
    deleteDoc, doc, updateDoc, serverTimestamp, where, getDocs, arrayUnion, writeBatch 
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { CubeIcon, EyeIcon, CloudArrowUpIcon, DocumentDownloadIcon } from '../../../components/icons/Icons';

// --- CONSTANTS ---
const ASSET_CATEGORIES = [
    "Laptop (Leased)", "Laptop (Owned)", "Monitor", "Headset", 
    "Keyboard", "Speaker", "Mouse", "Webcam", "Tablet", 
    "License / SaaS", 
    "Other"
];
const LAPTOP_CATEGORIES = ["Laptop (Leased)", "Laptop (Owned)"];
const ASSET_STATUSES = ["In Stock", "Active", "Retired", "Returned", "Under Repair", "Lost/Stolen"];

// --- ICONS ---
const ChevronUpIcon = () => <svg className="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>;
const ChevronDownIcon = () => <svg className="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;
const QuestionIcon = () => <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;

export default function ITInventory({ appId, userProfile }) {
    // Data States
    const [assets, setAssets] = useState([]);
    const [users, setUsers] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [bulkUploading, setBulkUploading] = useState(false);

    // UI States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const fileInputRef = useRef(null);

    // Table States
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [pagination, setPagination] = useState({ currentPage: 1, itemsPerPage: 20 });

    // Type-Ahead State
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const searchRef = useRef(null);

    // Form State
    const [formData, setFormData] = useState({
        assetTag: '', category: 'Laptop (Owned)', vendor: '', model: '',
        description: '', serialNumber: '', procurementDate: '', status: 'In Stock',
        assignmentType: 'unassigned', assignedToUserId: '', assignedToName: '', assignedToEmail: '',
        assignmentDate: '', cpu: '', ram: '', storage: '',
        url: '', username: ''
    });

    const [currentAssetData, setCurrentAssetData] = useState(null);

    // --- 1. FETCH DATA ---
    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/it_assets`), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(),
                searchStr: `${doc.data().assetTag} ${doc.data().category} ${doc.data().model || ''} ${doc.data().url || ''} ${doc.data().vendor} ${doc.data().status} ${doc.data().assignedToName || ''} ${doc.data().assignedToEmail || ''}`.toLowerCase()
            }));
            setAssets(data);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [appId]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const q = query(collection(db, `artifacts/${appId}/users`), where("status", "==", "Approved"));
                const snapshot = await getDocs(q);
                setUsers(snapshot.docs.map(doc => ({ uid: doc.data().uid || doc.id, name: doc.data().name, email: doc.data().email })));
            } catch (err) { console.error("Error fetching users", err); }
        };
        fetchUsers();
    }, [appId]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (searchRef.current && !searchRef.current.contains(event.target)) { setShowUserDropdown(false); }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- 2. TABLE LOGIC ---
    const filteredAssets = assets.filter(asset => asset.searchStr.includes(searchTerm.toLowerCase()));

    const sortedAssets = [...filteredAssets].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPages = Math.ceil(sortedAssets.length / pagination.itemsPerPage);
    const paginatedAssets = pagination.itemsPerPage === 'All' ? sortedAssets : sortedAssets.slice((pagination.currentPage - 1) * pagination.itemsPerPage, pagination.currentPage * pagination.itemsPerPage);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

   // --- 3. BULK UPLOAD & EXPORT LOGIC ---
    const handleExportData = () => {
        // 'sortedAssets' ensures it exports ALL pages, but respects your active search/sort
        const dataToExport = sortedAssets;

        if (dataToExport.length === 0) {
            alert("No assets found to export.");
            return;
        }

        const headers = [
            "Asset Tag", "Category", "Vendor", "Model or Plan", "Serial or Key", "Username or Email", "Login URL",
            "Status", "Date", "Description", "CPU", "RAM", "Storage" 
        ];

        const csvRows = [headers.join(",")];

        dataToExport.forEach(asset => {
            const row = [
                asset.assetTag || '',
                asset.category || '',
                asset.vendor || '',
                asset.model || '',
                asset.serialNumber || '',
                asset.username || '',
                asset.url || '',
                asset.status || '',
                asset.procurementDate || '',
                asset.description || '',
                asset.cpu || '',
                asset.ram || '',
                asset.storage || ''
            ];

            // Safely wrap every field in double quotes so commas in descriptions don't break the CSV
            const escapedRow = row.map(val => `"${String(val).replace(/"/g, '""')}"`);
            csvRows.push(escapedRow.join(','));
        });

        // --- NEW BLOB EXPORT METHOD ---
        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `IT_Inventory_Export_${new Date().toISOString().split('T')[0]}.csv`);
        
        document.body.appendChild(link);
        link.click();
        
        // Clean up the document and memory
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleBulkUploadClick = () => fileInputRef.current.click();

    const processBulkFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (evt) => {
            const csvText = evt.target.result;
            await parseAndUploadCSV(csvText);
        };
        reader.readAsText(file);
        e.target.value = null; 
    };

    const parseAndUploadCSV = async (csvText) => {
        setBulkUploading(true);
        try {
            const lines = csvText.split('\n').filter(line => line.trim() !== '');
            if (lines.length < 2) throw new Error("File appears empty or missing headers.");

            const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
            const dataRows = lines.slice(1);
            
            const keyMap = {
                'Tag': 'assetTag', 'Asset Tag': 'assetTag', 'ID': 'assetTag',
                'Category': 'category',
                'Vendor': 'vendor', 'Make': 'vendor',
                'Model': 'model', 'Model or Plan': 'model',
                'Serial': 'serialNumber', 'Serial Number': 'serialNumber', 'Serial or Key': 'serialNumber',
                'Username': 'username', 'Username or Email': 'username',
                'Login URL': 'url', 'URL': 'url', 'Link': 'url',
                'Date': 'procurementDate', 'Procurement Date': 'procurementDate',
                'Status': 'status',
                'Description': 'description', 'Notes': 'description',
                'CPU': 'cpu', 'Processor': 'cpu',
                'RAM': 'ram', 'Memory': 'ram',
                'Storage': 'storage', 'Disk': 'storage'
            };

            const batch = writeBatch(db);
            let newCount = 0;
            let updateCount = 0;
            const timestamp = new Date().toISOString();
            const actor = userProfile.name || 'Admin';
            
            // MAP EXISTING ASSETS BY TAG FOR QUICK LOOKUP
            const existingAssetsMap = {};
            assets.forEach(a => {
                existingAssetsMap[a.assetTag] = a;
            });

            dataRows.forEach(line => {
                const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
                if (values.length < 2) return; // Skip malformed rows

                const item = {};

                // Only add fields to the item if the CSV cell actually has data
                headers.forEach((header, index) => {
                    const internalKey = keyMap[header] || header.toLowerCase();
                    if (values[index]) item[internalKey] = values[index];
                });

                if (!item.assetTag) return; // Tag is mandatory

                // Enforce Laptop-only fields
                if (item.category && !LAPTOP_CATEGORIES.includes(item.category)) {
                    delete item.cpu; delete item.ram; delete item.storage;
                }

                const existingAsset = existingAssetsMap[item.assetTag];

                if (existingAsset) {
                    // --- 1. IT'S A BULK UPDATE ---
                    const ref = doc(db, `artifacts/${appId}/it_assets`, existingAsset.id);
                    item.updatedAt = serverTimestamp();
                    item.history = arrayUnion({ 
                        date: timestamp, 
                        action: 'Bulk Update', 
                        details: 'Updated via CSV import', 
                        by: actor 
                    });
                    
                    batch.update(ref, item);
                    updateCount++;
                } else {
                    // --- 2. IT'S A NEW BULK CREATION ---
                    if (!item.category) item.category = 'Other';
                    item.status = item.status || 'In Stock'; 
                    item.assignmentType = item.assignmentType || 'unassigned';
                    item.createdAt = serverTimestamp(); 
                    item.updatedAt = serverTimestamp(); 
                    item.createdBy = userProfile.uid;
                    item.history = [{ 
                        date: timestamp, 
                        action: 'Bulk Upload', 
                        details: 'Imported via CSV', 
                        by: actor 
                    }];

                    const ref = doc(collection(db, `artifacts/${appId}/it_assets`));
                    batch.set(ref, item);
                    newCount++;
                }
            });

            if (newCount > 0 || updateCount > 0) {
                await batch.commit();
                alert(`Success! Created ${newCount} new assets and updated ${updateCount} existing assets.`);
            } else {
                alert("No valid assets found in the file.");
            }

        } catch (error) {
            console.error("Bulk Upload Error:", error);
            alert("Failed to process CSV: " + error.message);
        } finally {
            setBulkUploading(false);
        }
    };

    // --- 4. FORM HANDLERS ---
    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        let updates = { status: newStatus };
        if (newStatus !== 'Active') {
            updates.assignmentType = 'unassigned';
            updates.assignedToUserId = ''; updates.assignedToName = ''; updates.assignedToEmail = ''; updates.assignmentDate = '';
            setUserSearchTerm('');
        }
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const handleAssignmentTypeChange = (e) => {
        const newType = e.target.value;
        const todayISO = new Date().toISOString().split('T')[0];
        let updates = { assignmentType: newType };
        if (newType === 'internal' || newType === 'external') {
            updates.status = 'Active'; updates.assignmentDate = todayISO;
        } else {
            updates.status = 'In Stock';
            updates.assignedToUserId = ''; updates.assignedToName = ''; updates.assignedToEmail = '';
            updates.assignmentDate = ''; setUserSearchTerm('');
        }
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const handleUserSearch = (e) => {
        setUserSearchTerm(e.target.value);
        setShowUserDropdown(true);
        if (formData.assignedToUserId) setFormData(prev => ({ ...prev, assignedToUserId: '', assignedToName: '', assignedToEmail: '' }));
    };

    const selectUser = (user) => {
        setFormData(prev => ({ ...prev, assignedToUserId: user.uid, assignedToName: user.name, assignedToEmail: user.email }));
        setUserSearchTerm(user.name);
        setShowUserDropdown(false);
    };

    const handleEditClick = (asset) => {
        setEditingId(asset.id);
        setCurrentAssetData(asset);
        setFormData({
            assetTag: asset.assetTag || '', category: asset.category || 'Laptop (Owned)',
            vendor: asset.vendor || '', model: asset.model || '', description: asset.description || '',
            serialNumber: asset.serialNumber || '', procurementDate: asset.procurementDate || '',
            status: asset.status || 'In Stock', assignmentType: asset.assignmentType || 'unassigned',
            assignedToUserId: asset.assignedToUserId || '', assignedToName: asset.assignedToName || '',
            assignedToEmail: asset.assignedToEmail || '', assignmentDate: asset.assignmentDate || '', 
            cpu: asset.cpu || '', ram: asset.ram || '', storage: asset.storage || '',
            url: asset.url || '', username: asset.username || ''
        });
        if (asset.assignmentType === 'internal' && asset.assignedToName) setUserSearchTerm(asset.assignedToName);
        else setUserSearchTerm('');
        setIsModalOpen(true);
    };

    const handleAddNewClick = () => {
        setEditingId(null); setCurrentAssetData(null); resetForm();
        setFormData(prev => ({ ...prev, procurementDate: new Date().toISOString().split('T')[0] }));
        setUserSearchTerm(''); setIsModalOpen(true);
    };

    const generateHistoryLog = (oldData, newData, isNew) => {
        const logs = [];
        const timestamp = new Date().toISOString();
        const actor = userProfile.name || 'Admin';
        if (isNew) {
            logs.push({ date: timestamp, action: 'Procured', details: `Asset created. Status: ${newData.status}`, by: actor });
            if (newData.assignmentType !== 'unassigned') logs.push({ date: timestamp, action: 'Assigned', details: `Assigned to ${newData.assignedToName}`, by: actor });
        } else {
            if (oldData.status !== newData.status) logs.push({ date: timestamp, action: 'Status Change', details: `Changed from ${oldData.status} to ${newData.status}`, by: actor });
            if (oldData.assignedToName !== newData.assignedToName) {
                if (newData.assignmentType === 'unassigned') logs.push({ date: timestamp, action: 'Unassigned', details: `Returned from ${oldData.assignedToName || 'Unknown'}`, by: actor });
                else logs.push({ date: timestamp, action: 'Assigned', details: `Assigned to ${newData.assignedToName}`, by: actor });
            }
        }
        return logs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const isSaaS = formData.category === 'License / SaaS';
            if (!formData.assetTag) { alert("Asset Tag / ID is required."); return; }
            if (!isSaaS && !formData.serialNumber) { alert("Serial Number is required for hardware assets."); return; }

            const exists = assets.find(a => a.assetTag === formData.assetTag && a.id !== editingId);
            if (exists) { alert("Asset Tag / ID must be unique."); return; }

            let finalAssignedName = '';
            if (formData.assignmentType === 'internal') {
                if (!formData.assignedToUserId) { alert("Please select an employee."); return; }
                finalAssignedName = formData.assignedToName;
            } else if (formData.assignmentType === 'external') {
                finalAssignedName = formData.assignedToName;
                formData.assignedToEmail = '';
            }

            const payload = { ...formData, assignedToName: finalAssignedName, updatedAt: serverTimestamp() };
            
            // SAAS MAPPING PRESERVATION FIX
            if (isSaaS) {
                delete payload.cpu; delete payload.ram; delete payload.storage; 
                if(!payload.serialNumber) payload.serialNumber = 'N/A';
            } else {
                delete payload.url; delete payload.username;
            }
            if (!LAPTOP_CATEGORIES.includes(formData.category)) { delete payload.cpu; delete payload.ram; delete payload.storage; }

            const newHistoryEntries = generateHistoryLog(currentAssetData, payload, !editingId);
            if (editingId) {
                const docRef = doc(db, `artifacts/${appId}/it_assets`, editingId);
                if (newHistoryEntries.length > 0) payload.history = arrayUnion(...newHistoryEntries);
                await updateDoc(docRef, payload);
            } else {
                payload.createdAt = serverTimestamp(); payload.createdBy = userProfile.uid; payload.history = newHistoryEntries;
                await addDoc(collection(db, `artifacts/${appId}/it_assets`), payload);
            }
            setIsModalOpen(false); resetForm();
        } catch (error) { console.error("Error saving asset:", error); alert("Failed to save asset."); }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure?")) await deleteDoc(doc(db, `artifacts/${appId}/it_assets`, id));
    };

    const resetForm = () => {
        setFormData({
            assetTag: '', category: 'Laptop (Owned)', vendor: '', model: '', description: '', serialNumber: '', 
            procurementDate: '', status: 'In Stock', assignmentType: 'unassigned', assignedToUserId: '', 
            assignedToName: '', assignedToEmail: '', assignmentDate: '', cpu: '', ram: '', storage: '', url: '', username: ''
        });
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'Active': return 'bg-green-100 text-green-800';
            case 'In Stock': return 'bg-blue-100 text-blue-800';
            case 'Retired': return 'bg-gray-100 text-gray-800';
            case 'Returned': return 'bg-yellow-100 text-yellow-800';
            case 'Lost/Stolen': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const filteredUsers = users.filter(u => u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || u.email.toLowerCase().includes(userSearchTerm.toLowerCase()));
    const isSaaS = formData.category === 'License / SaaS';

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <h3 className="text-lg font-bold text-[#213368]">Asset Inventory</h3>
                <div className="flex-1 w-full md:max-w-md">
                    <input 
                        type="text" 
                        placeholder="Search by tag, vendor, model, user..." 
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setPagination(prev => ({...prev, currentPage: 1})); }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                </div>
                
                {/* --- ACTION BUTTONS --- */}
                <div className="flex flex-wrap gap-2 justify-end">
                    
                    {/* Data Map Help Button */}
                    <button 
                        onClick={() => setIsHelpModalOpen(true)}
                        className="flex items-center px-4 py-2 bg-yellow-50 border border-yellow-200 text-yellow-800 hover:bg-yellow-100 rounded-lg shadow-sm transition-colors text-sm font-medium whitespace-nowrap"
                        title="View Field Guide"
                    >
                        <QuestionIcon /> Field Guide
                    </button>

                    <input type="file" ref={fileInputRef} style={{display: 'none'}} accept=".csv" onChange={processBulkFile} />
                    
                    <button onClick={handleExportData} className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg shadow-sm transition-colors text-sm font-medium whitespace-nowrap">
                        <DocumentDownloadIcon className="w-4 h-4 mr-2" /> Export / Edit Data
                    </button>

                    <button onClick={handleBulkUploadClick} disabled={bulkUploading} className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg shadow-sm transition-colors text-sm font-medium whitespace-nowrap">
                        {bulkUploading ? 'Uploading...' : <><CloudArrowUpIcon className="w-4 h-4 mr-2" /> Bulk Upload</>}
                    </button>

                    <button onClick={handleAddNewClick} className="flex items-center px-4 py-2 bg-[#20BEC6] hover:bg-[#1ba8af] text-white rounded-lg shadow-sm transition-colors text-sm font-medium whitespace-nowrap">
                        <CubeIcon className="w-4 h-4 mr-2" /> Add Asset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b select-none">
                            <tr>
                                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('category')}>Category {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? <ChevronUpIcon/> : <ChevronDownIcon/>)}</th>
                                {/* Added max-w to control tag width */}
<th className="px-6 py-3 cursor-pointer max-w-[150px]" onClick={() => handleSort('assetTag')}>Tag / ID {sortConfig.key === 'assetTag' && (sortConfig.direction === 'asc' ? <ChevronUpIcon/> : <ChevronDownIcon/>)}</th>
<th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('vendor')}>Vendor {sortConfig.key === 'vendor' && (sortConfig.direction === 'asc' ? <ChevronUpIcon/> : <ChevronDownIcon/>)}</th>
{/* Added min-w to protect details width */}
<th className="px-6 py-3 min-w-[200px]">Details (Model / Plan)</th>
                                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('assignedToName')}>Assigned To {sortConfig.key === 'assignedToName' && (sortConfig.direction === 'asc' ? <ChevronUpIcon/> : <ChevronDownIcon/>)}</th>
                                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('status')}>Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? <ChevronUpIcon/> : <ChevronDownIcon/>)}</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="p-4 text-center">Loading assets...</td></tr>
                            ) : paginatedAssets.length === 0 ? (
                                <tr><td colSpan="7" className="p-8 text-center text-gray-400">No assets found.</td></tr>
                            ) : (
                                paginatedAssets.map((asset) => (
                                    <tr key={asset.id} className="bg-white border-b hover:bg-gray-50">
                                        <td className="px-6 py-4 font-medium text-[#213368]">{asset.category}</td>
{/* Added max-w, truncate, and a title tooltip for hovering */}
<td className="px-6 py-4 font-mono text-xs text-gray-900 max-w-[150px] truncate" title={asset.assetTag}>
    {asset.assetTag}
</td>                                        <td className="px-6 py-4 text-gray-700">{asset.vendor || '--'}</td>
                                        <td className="px-6 py-4">
                                            {asset.category === 'License / SaaS' ? (
                                                <div className="flex flex-col">
                                                    <div className="font-medium">{asset.model || asset.url || 'SaaS License'}</div>
                                                    <span className="text-xs text-gray-400">{asset.username || 'No Email Logged'}</span>
                                                </div>
                                            ) : (
                                                <div className="font-medium">{asset.model || '--'}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {asset.assignedToName ? (
                                                <div>
                                                    <div className="text-gray-900 font-medium">{asset.assignedToName}</div>
                                                    {asset.assignedToEmail && <div className="text-xs text-gray-500">{asset.assignedToEmail}</div>}
                                                </div>
                                            ) : <span className="text-gray-400 italic">--</span>}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(asset.status)}`}>{asset.status}</span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button onClick={() => handleEditClick(asset)} className="text-blue-600 hover:text-blue-900"><EyeIcon className="w-5 h-5" /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Pagination */}
                <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50">
                     <div className="flex items-center text-sm text-gray-600">
                        <select value={pagination.itemsPerPage} onChange={(e) => setPagination({ currentPage: 1, itemsPerPage: e.target.value === 'All' ? 'All' : Number(e.target.value) })} className="mr-2 p-1 border rounded">
                            <option value={20}>20</option><option value={50}>50</option><option value="All">All</option>
                        </select>
                        Rows | Showing {pagination.itemsPerPage === 'All' ? filteredAssets.length : Math.min(pagination.currentPage * pagination.itemsPerPage, filteredAssets.length)} of {filteredAssets.length}
                     </div>
                     <div className="flex gap-2">
                        <button onClick={() => setPagination(prev => ({...prev, currentPage: prev.currentPage - 1}))} disabled={pagination.currentPage === 1} className="px-3 py-1 border rounded bg-white disabled:bg-gray-100">Prev</button>
                        <button onClick={() => setPagination(prev => ({...prev, currentPage: prev.currentPage + 1}))} disabled={pagination.currentPage >= totalPages || pagination.itemsPerPage === 'All'} className="px-3 py-1 border rounded bg-white disabled:bg-gray-100">Next</button>
                     </div>
                </div>
            </div>

            {/* Field Guide Help Modal */}
            {isHelpModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
                        <div className="bg-yellow-50 border-b border-yellow-200 p-4 flex justify-between items-center">
                            <h2 className="text-lg font-bold text-yellow-800 flex items-center"><QuestionIcon /> IT Inventory Data Mapping Guide</h2>
                            <button onClick={() => setIsHelpModalOpen(false)} className="text-yellow-600 hover:text-yellow-900 font-bold">✕</button>
                        </div>
                        <div className="p-6 space-y-6">
                            <p className="text-sm text-gray-600">The system automatically adapts the entry fields based on whether you are tracking physical hardware or a software license. Here is how the data is mapped:</p>
                            
                            <div className="grid grid-cols-2 gap-6">
                                <div className="border border-blue-100 bg-blue-50/50 p-4 rounded-lg">
                                    <h3 className="font-bold text-[#213368] mb-3 border-b border-blue-100 pb-2">💻 Physical Hardware</h3>
                                    <ul className="text-sm space-y-3 text-gray-700">
                                        <li><strong>Asset Tag:</strong> Unique tracking barcode/sticker (e.g., <span className="font-mono bg-white px-1">LAP-104</span>). Must be unique.</li>
                                        <li><strong>Model:</strong> The device model name (e.g., ThinkPad T14).</li>
                                        <li><strong>Serial Number:</strong> The manufacturer's SN etched on the device.</li>
                                        <li><strong>Vendor:</strong> Where it was bought (e.g., Dell, Amazon).</li>
                                    </ul>
                                </div>
                                <div className="border border-purple-100 bg-purple-50/50 p-4 rounded-lg">
                                    <h3 className="font-bold text-purple-800 mb-3 border-b border-purple-100 pb-2">☁️ License / SaaS</h3>
                                    <ul className="text-sm space-y-3 text-gray-700">
                                        <li><strong>License ID / Tag:</strong> Internal tracking code (e.g., <span className="font-mono bg-white px-1">LIC-001</span>). Must be unique.</li>
                                        <li><strong>Plan / Sub Type:</strong> What tier of software this is (e.g., Pro, Enterprise).</li>
                                        <li><strong>Username / Email:</strong> The account login (e.g., john@company.com). <em>Can be reused across different licenses.</em></li>
                                        <li><strong>License Key:</strong> Software activation code (if applicable).</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="text-center">
                                <button onClick={() => setIsHelpModalOpen(false)} className="px-6 py-2 bg-[#213368] text-white rounded-lg hover:bg-opacity-90 font-medium">Got It</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Asset Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b flex justify-between items-center bg-[#213368] text-white">
                            <div className="flex items-center gap-4">
                                <h2 className="text-xl font-bold">{editingId ? 'Edit Asset' : 'New Asset'}</h2>
                                <button 
                                    type="button"
                                    onClick={() => setIsHelpModalOpen(true)}
                                    className="flex items-center text-xs font-medium bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full transition-colors"
                                >
                                    <QuestionIcon /> Field Guide
                                </button>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="hover:text-gray-300 font-bold">✕</button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            
                            {/* ALLOCATION BLOCK */}
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Allocation Type</label>
                                        <select name="assignmentType" value={formData.assignmentType} onChange={handleAssignmentTypeChange} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none">
                                            <option value="unassigned">Unassigned</option>
                                            <option value="internal">Internal Employee</option>
                                            <option value="external">External Party</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Status</label>
                                        <select name="status" value={formData.status} onChange={handleStatusChange} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none">
                                            {ASSET_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                </div>
                                {formData.assignmentType !== 'unassigned' && (
                                    <div className="mt-4 grid grid-cols-2 gap-4">
                                        <div className="relative" ref={searchRef}>
                                            <label className="block text-sm font-medium mb-1">{formData.assignmentType === 'internal' ? 'Employee' : 'Name'}</label>
                                            {formData.assignmentType === 'internal' ? (
                                                <>
                                                    <input type="text" placeholder="Search employee..." value={userSearchTerm} onChange={handleUserSearch} onFocus={() => setShowUserDropdown(true)} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                                                    {showUserDropdown && (
                                                        <div className="absolute z-10 w-full bg-white mt-1 border rounded shadow-lg max-h-40 overflow-y-auto">
                                                            {filteredUsers.length > 0 ? (
                                                                filteredUsers.map(u => (
                                                                    <div key={u.uid} onClick={() => selectUser(u)} className="p-2 hover:bg-blue-50 cursor-pointer text-sm border-b last:border-0">
                                                                        <div className="font-medium text-gray-900">{u.name}</div>
                                                                        <div className="text-xs text-gray-500">{u.email}</div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="p-2 text-sm text-gray-400">No users found</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <input type="text" name="assignedToName" value={formData.assignedToName} onChange={(e) => setFormData({...formData, assignedToName: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" required />
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Assignment Date</label>
                                            <input type="date" name="assignmentDate" value={formData.assignmentDate} onChange={(e) => setFormData({...formData, assignmentDate: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" required />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* CORE DETAILS */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Category</label>
                                    <select name="category" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none">
                                        {ASSET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{isSaaS ? 'License ID / Tag' : 'Asset Tag'}</label>
                                    <input type="text" value={formData.assetTag} onChange={(e) => setFormData({...formData, assetTag: e.target.value})} placeholder={isSaaS ? "e.g. LIC-001" : ""} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" required />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Vendor / Provider</label>
                                    <input type="text" value={formData.vendor} onChange={(e) => setFormData({...formData, vendor: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Date Procured</label>
                                    <input type="date" value={formData.procurementDate} onChange={(e) => setFormData({...formData, procurementDate: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                                </div>
                            </div>

                            {/* CONDITIONAL DETAILS (SaaS vs Hardware) */}
                            {isSaaS ? (
                                <div className="bg-purple-50 p-4 rounded border border-purple-100 grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium mb-1 text-purple-800">Login URL</label>
                                        <input type="text" value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} placeholder="https://..." className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-300 outline-none" />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-sm font-medium mb-1 text-purple-800">Plan / Subscription Type</label>
                                        <input type="text" value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-300 outline-none" placeholder="e.g. Pro, Enterprise" />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="block text-sm font-medium mb-1 text-purple-800">Account Username / Email</label>
                                        <input type="text" value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-300 outline-none" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium mb-1 text-purple-800">License Key (Optional)</label>
                                        <input type="text" value={formData.serialNumber} onChange={(e) => setFormData({...formData, serialNumber: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-300 outline-none" />
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Model</label>
                                        <input type="text" value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Serial Number</label>
                                        <input type="text" value={formData.serialNumber} onChange={(e) => setFormData({...formData, serialNumber: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" required />
                                    </div>
                                </div>
                            )}
                            
                            {/* LAPTOP SPECS */}
                            {!isSaaS && LAPTOP_CATEGORIES.includes(formData.category) && (
                                <div className="grid grid-cols-3 gap-2 bg-blue-50 p-2 rounded border border-blue-100">
                                    <input type="text" placeholder="CPU" value={formData.cpu} onChange={(e) => setFormData({...formData, cpu: e.target.value})} className="p-2 border rounded text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                                    <input type="text" placeholder="RAM" value={formData.ram} onChange={(e) => setFormData({...formData, ram: e.target.value})} className="p-2 border rounded text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                                    <input type="text" placeholder="Storage" value={formData.storage} onChange={(e) => setFormData({...formData, storage: e.target.value})} className="p-2 border rounded text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium mb-1">Description / Notes</label>
                                <textarea rows="2" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full p-2 border rounded focus:ring-2 focus:ring-[#20BEC6] outline-none" />
                            </div>

                            {/* HISTORY */}
                            {editingId && currentAssetData?.history?.length > 0 && (
                                <div className="border-t pt-4">
                                    <h4 className="text-sm font-bold text-[#213368] mb-2">History</h4>
                                    <div className="max-h-32 overflow-y-auto space-y-2 text-xs text-gray-600">
                                        {[...currentAssetData.history].reverse().map((log, i) => (
                                            <div key={i} className="flex gap-2">
                                                <span className="min-w-[80px] text-gray-400">{log.date.split('T')[0]}</span>
                                                <span className="font-medium">{log.action}:</span>
                                                <span>{log.details}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
                                <button type="submit" className="px-6 py-2 bg-[#20BEC6] text-white rounded hover:bg-[#1ba8af] font-medium shadow-sm">{editingId ? 'Update' : 'Save'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}